<template>
  <div class="pagos-agrupados-view" :style="modalDynamicStyles">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div v-if="admin_datos.superadmin" class="main-container">
        <div class="articulo-principal pagos-agrupados">
          <div class="pagos-agrupados__header">
            <div>
              <h2 class="pagos-agrupados__title">Revisión de pagos agrupados</h2>
              <p class="pagos-agrupados__subtitle">
                Revisa las solicitudes de pago enviadas por administradores de propiedades, valida los comprobantes y aprueba o rechaza cada lote.
              </p>
            </div>
            <div class="pagos-agrupados__filters">
              <b-form-select
                v-model="estadoFiltro"
                :options="estadoOptions"
                size="sm"
                class="pagos-agrupados__select"
                :disabled="loading"
              />
              <b-button
                size="sm"
                variant="outline-primary"
                class="pagos-agrupados__refresh"
                :disabled="loading"
                @click="fetchSolicitudes"
              >
                Actualizar
              </b-button>
            </div>
          </div>

          <b-alert
            v-if="errorMessage"
            variant="danger"
            dismissible
            @dismissed="errorMessage = ''"
            class="mb-3"
          >
            {{ errorMessage }}
          </b-alert>

          <div v-if="loading" class="pagos-agrupados__loading">
            <b-spinner small class="mr-2" /> Cargando solicitudes...
          </div>

          <div v-else class="pagos-agrupados__body">
            <div class="pagos-agrupados__list" v-if="solicitudes.length">
              <div
                v-for="solicitud in solicitudes"
                :key="`solicitud-${solicitud.id}`"
                class="solicitud-card"
                :class="{ 'solicitud-card--selected': solicitudSeleccionada && solicitudSeleccionada.id === solicitud.id }"
                @click="selectSolicitud(solicitud)"
              >
                <div class="solicitud-card__header">
                  <h3 class="solicitud-card__title">Solicitud #{{ solicitud.id }}</h3>
                  <b-badge :variant="estadoBadgeVariant(solicitud.estado)">
                    {{ solicitud.estado_label || 'Sin estado' }}
                  </b-badge>
                </div>
                <div class="solicitud-card__row">
                  <span class="solicitud-card__label">Administrador</span>
                  <span class="solicitud-card__value">
                    {{ solicitud.administrador ? solicitud.administrador.nombre : 'Sin nombre' }}
                  </span>
                </div>
                <div class="solicitud-card__row">
                  <span class="solicitud-card__label">Empresa</span>
                  <span class="solicitud-card__value">
                    {{ solicitud.administrador && solicitud.administrador.empresa ? solicitud.administrador.empresa.nombre : 'Sin empresa' }}
                  </span>
                </div>
                <div class="solicitud-card__row">
                  <span class="solicitud-card__label">Monto</span>
                  <span class="solicitud-card__value solicitud-card__value--strong">
                    {{ formatCurrency(solicitud.monto_total) }}
                  </span>
                </div>
                <div class="solicitud-card__row">
                  <span class="solicitud-card__label">Recibida</span>
                  <span class="solicitud-card__value">
                    {{ formatDate(solicitud.fecha_creado) }}
                  </span>
                </div>
              </div>
            </div>

            <div v-else class="pagos-agrupados__empty">
              No se encontraron solicitudes con el filtro seleccionado.
            </div>

            <div v-if="solicitudSeleccionada" class="pagos-agrupados__detail">
              <div class="pagos-agrupados__detail-header">
                <div>
                  <h3 class="pagos-agrupados__detail-title">Solicitud #{{ solicitudSeleccionada.id }}</h3>
                  <div class="pagos-agrupados__detail-meta">
                    <b-badge :variant="estadoBadgeVariant(solicitudSeleccionada.estado)">
                      {{ solicitudSeleccionada.estado_label || 'Sin estado' }}
                    </b-badge>
                    <span class="pagos-agrupados__detail-date">
                      Creada: {{ formatDate(solicitudSeleccionada.fecha_creado) }}
                    </span>
                    <span
                      v-if="solicitudSeleccionada.fecha_resuelto"
                      class="pagos-agrupados__detail-date"
                    >
                      Revisada: {{ formatDate(solicitudSeleccionada.fecha_resuelto) }}
                    </span>
                  </div>
                </div>
                <div v-if="solicitudSeleccionada.estado === ESTADO_PAGO_EN_REVISION" class="pagos-agrupados__actions">
                  <b-button
                    size="sm"
                    variant="success"
                    :disabled="processingDecision"
                    @click="openRevisionModal('aprobar')"
                  >
                    Aprobar
                  </b-button>
                  <b-button
                    size="sm"
                    variant="danger"
                    class="ml-2"
                    :disabled="processingDecision"
                    @click="openRevisionModal('rechazar')"
                  >
                    Rechazar
                  </b-button>
                </div>
              </div>

              <b-alert
                v-if="feedbackMessage"
                :variant="feedbackVariant"
                dismissible
                @dismissed="feedbackMessage = ''"
                class="mt-3"
              >
                {{ feedbackMessage }}
              </b-alert>

              <section class="detail-section">
                <h4>Resumen</h4>
                <div class="detail-grid">
                  <div>
                    <div class="detail-label">Administrador</div>
                    <div class="detail-value">
                      {{ solicitudSeleccionada.administrador ? solicitudSeleccionada.administrador.nombre : 'Sin nombre' }}
                    </div>
                    <div v-if="solicitudSeleccionada.administrador" class="detail-subvalue">
                      {{ solicitudSeleccionada.administrador.email || 'Sin email' }} ·
                      {{ solicitudSeleccionada.administrador.telefono || 'Sin teléfono' }}
                    </div>
                  </div>
                  <div>
                    <div class="detail-label">Empresa</div>
                    <div class="detail-value">
                      {{ solicitudSeleccionada.administrador && solicitudSeleccionada.administrador.empresa ? solicitudSeleccionada.administrador.empresa.nombre : 'Sin empresa' }}
                    </div>
                  </div>
                  <div>
                    <div class="detail-label">Monto total</div>
                    <div class="detail-value detail-value--strong">
                      {{ formatCurrency(solicitudSeleccionada.monto_total) }}
                    </div>
                  </div>
                  <div>
                    <div class="detail-label">Notas del administrador</div>
                    <div class="detail-value">
                      {{ solicitudSeleccionada.notas ? solicitudSeleccionada.notas : 'Sin notas adicionales.' }}
                    </div>
                  </div>
                  <div v-if="solicitudSeleccionada.observaciones_revision">
                    <div class="detail-label">Observaciones internas</div>
                    <div class="detail-value">
                      {{ solicitudSeleccionada.observaciones_revision }}
                    </div>
                  </div>
                  <div v-if="solicitudSeleccionada.revisor">
                    <div class="detail-label">Revisado por</div>
                    <div class="detail-value">
                      {{ solicitudSeleccionada.revisor.nombre || solicitudSeleccionada.revisor.email }}
                    </div>
                  </div>
                </div>
              </section>

              <section class="detail-section">
                <h4>Comprobantes</h4>
                <ul class="comprobantes-list" v-if="solicitudSeleccionada.comprobantes && solicitudSeleccionada.comprobantes.length">
                  <li v-for="archivo in solicitudSeleccionada.comprobantes" :key="`archivo-${archivo.id}`">
                    <div>
                      <div class="comprobante-nombre">{{ archivo.nombre_archivo }}</div>
                      <div class="comprobante-meta">
                        Subido: {{ formatDate(archivo.fecha_subido) }}
                      </div>
                    </div>
                    <div>
                      <a
                        v-if="archivo.url_descarga"
                        :href="archivo.url_descarga"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Descargar
                      </a>
                      <span v-else class="comprobante-meta">Link no disponible</span>
                    </div>
                  </li>
                </ul>
                <div v-else class="detail-empty">No hay comprobantes adjuntos.</div>
              </section>

              <section class="detail-section">
                <h4>Órdenes incluidas</h4>
                <div v-if="solicitudSeleccionada.pagos && solicitudSeleccionada.pagos.length" class="table-responsive">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th>Orden</th>
                        <th>Cliente</th>
                        <th>Dirección</th>
                        <th>Profesional</th>
                        <th class="text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="pago in solicitudSeleccionada.pagos" :key="`pago-${pago.id}`">
                        <td>#{{ pago.orden_id || 'N/D' }}</td>
                        <td>
                          <div class="table-main">
                            {{ pago.orden && pago.orden.solicitud && pago.orden.solicitud.titulo ? pago.orden.solicitud.titulo : 'Solicitud sin título' }}
                          </div>
                          <div v-if="pago.orden && pago.orden.solicitud" class="table-sub">
                            {{ pago.orden.solicitud.nombre }} {{ pago.orden.solicitud.apellido || '' }}
                          </div>
                        </td>
                        <td>
                          <div class="table-main">
                            {{ pago.orden && pago.orden.direccion ? pago.orden.direccion : 'Sin dirección' }}
                          </div>
                          <div class="table-sub">
                            {{ pago.orden && pago.orden.comuna ? pago.orden.comuna : 'Sin comuna' }}
                          </div>
                        </td>
                        <td>
                          <div class="table-main">
                            {{ pago.orden && pago.orden.profesional ? `${pago.orden.profesional.nombre || ''} ${pago.orden.profesional.apellido || ''}`.trim() : 'Sin profesional' }}
                          </div>
                        </td>
                        <td class="text-right">{{ formatCurrency(pago.monto) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="detail-empty">Esta solicitud no tiene pagos asociados.</div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="modal-fade">
      <div v-if="showRevisionModal" class="revision-modal-overlay">
        <div class="revision-modal-container">
          <div class="revision-modal-header">
            <h3 class="revision-modal-title">{{ modalTitle }}</h3>
            <button
              type="button"
              class="close-btn"
              :disabled="processingDecision"
              @click="closeRevisionModal"
            >
              ×
            </button>
          </div>

          <form class="revision-modal-form" @submit.prevent="submitRevisionDecision">
            <div class="revision-modal-body">
              <p class="revision-modal-intro">
                Confirma que deseas
                <strong>{{ modalDecision === 'aprobar' ? 'aprobar' : 'rechazar' }}</strong>
                la solicitud de pago agrupado
                #{{ solicitudSeleccionada ? solicitudSeleccionada.id : '' }}.
              </p>

              <div class="input-group">
                <label class="input-label" for="observacionesRevision">Observaciones</label>
                <textarea
                  id="observacionesRevision"
                  v-model="modalObservaciones"
                  class="input-textarea"
                  :disabled="processingDecision"
                  rows="3"
                  placeholder="Opcional: agrega comentarios para el historial interno"
                />
              </div>

              <div v-if="modalError" class="modal-error">{{ modalError }}</div>
            </div>

            <div class="revision-modal-actions">
              <b-button
                type="button"
                variant="light"
                :disabled="processingDecision"
                @click="closeRevisionModal"
              >
                Cancelar
              </b-button>
              <b-button
                type="submit"
                :variant="modalDecision === 'aprobar' ? 'success' : 'danger'"
                :disabled="processingDecision"
              >
                {{ modalDecision === 'aprobar' ? 'Aprobar solicitud' : 'Rechazar solicitud' }}
              </b-button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';
import { BFormSelect, BButton, BBadge, BAlert, BSpinner } from 'bootstrap-vue';

const ESTADO_PAGO_PENDIENTE = 0;
const ESTADO_PAGO_PAGADO = 1;
const ESTADO_PAGO_REVERSADO = 2;
const ESTADO_PAGO_EN_REVISION = 3;

export default {
  name: 'PagosAgrupadosSuperadmin',
  components: {
    SideNav,
    TopNav,
    BFormSelect,
    BButton,
    BBadge,
    BAlert,
    BSpinner,
  },
  data() {
    return {
      solicitudes: [],
      loading: false,
      errorMessage: '',
      estadoFiltro: 'en_revision',
      solicitudSeleccionada: null,
      feedbackMessage: '',
      feedbackVariant: 'success',
      modalDecision: null,
      modalObservaciones: '',
      processingDecision: false,
      showRevisionModal: false,
      modalError: '',
      bodyZoom: 1,
      ESTADO_PAGO_EN_REVISION,
    };
  },
  computed: {
    ...mapGetters(['admin_datos']),
    estadoOptions() {
      return [
        { value: 'en_revision', text: 'Pendientes de revisión' },
        { value: 'pagado', text: 'Aprobados' },
        { value: 'reversado', text: 'Rechazados' },
        { value: 'todos', text: 'Todos' },
      ];
    },
    modalTitle() {
      if (!this.modalDecision) {
        return 'Revisión de solicitud';
      }
      return this.modalDecision === 'aprobar' ? 'Aprobar solicitud' : 'Rechazar solicitud';
    },
    modalDynamicStyles() {
      const zoom = Number(this.bodyZoom) || 1;
      const scale = zoom > 0 && zoom < 1 ? 1 / zoom : 1;
      return {
        '--pagos-agrupados-modal-scale': scale,
      };
    },
  },
  watch: {
    estadoFiltro(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchSolicitudes();
      }
    },
  },
  created() {
    this.initialize_();
  },
  mounted() {
    this.captureBodyZoom();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.captureBodyZoom);
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.captureBodyZoom);
    }
  },
  methods: {
    ...mapActions(['initialize']),
    initialize_() {
      this.initialize();
      this.fetchSolicitudes();
    },
    fetchSolicitudes() {
      this.loading = true;
      this.errorMessage = '';
      const params = {};
      if (this.estadoFiltro) {
        params.estado = this.estadoFiltro;
      }

      axios
        .get(`${window.hostname}/api/superadmin/pagos_agrupados`, { params })
        .then((response) => {
          const solicitudes = response.data && Array.isArray(response.data.solicitudes)
            ? response.data.solicitudes
            : [];
          this.solicitudes = solicitudes;
          const currentId = this.solicitudSeleccionada ? this.solicitudSeleccionada.id : null;
          if (currentId) {
            this.solicitudSeleccionada = solicitudes.find((s) => s.id === currentId) || (solicitudes.length ? solicitudes[0] : null);
          } else {
            this.solicitudSeleccionada = solicitudes.length ? solicitudes[0] : null;
          }
        })
        .catch((error) => {
          console.error('Error al obtener los pagos agrupados', error);
          this.errorMessage = 'No se pudieron obtener las solicitudes de pago agrupado.';
          this.solicitudes = [];
          this.solicitudSeleccionada = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectSolicitud(solicitud) {
      this.solicitudSeleccionada = solicitud;
      this.feedbackMessage = '';
    },
    estadoBadgeVariant(estado) {
      switch (estado) {
        case ESTADO_PAGO_EN_REVISION:
          return 'warning';
        case ESTADO_PAGO_PAGADO:
          return 'success';
        case ESTADO_PAGO_REVERSADO:
          return 'danger';
        case ESTADO_PAGO_PENDIENTE:
          return 'secondary';
        default:
          return 'secondary';
      }
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },
    formatDate(value) {
      if (!value) {
        return 'Sin registro';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return 'Sin registro';
      }
      return date.toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    openRevisionModal(decision) {
      this.modalDecision = decision;
      this.modalObservaciones = '';
      this.processingDecision = false;
      this.modalError = '';
      this.captureBodyZoom();
      this.showRevisionModal = true;
    },
    closeRevisionModal() {
      if (this.processingDecision) {
        return;
      }
      this.resetRevisionModal();
    },
    submitRevisionDecision() {
      if (!this.solicitudSeleccionada || !this.modalDecision || this.processingDecision) {
        return;
      }
      this.processingDecision = true;
      this.modalError = '';
      axios
        .post(`${window.hostname}/api/superadmin/pagos_agrupados/${this.solicitudSeleccionada.id}/revision`, {
          decision: this.modalDecision,
          observaciones: this.modalObservaciones,
        })
        .then(() => {
          this.feedbackMessage = this.modalDecision === 'aprobar'
            ? 'La solicitud fue aprobada exitosamente.'
            : 'La solicitud fue rechazada exitosamente.';
          this.feedbackVariant = this.modalDecision === 'aprobar' ? 'success' : 'danger';
          this.resetRevisionModal();
          this.fetchSolicitudes();
        })
        .catch((error) => {
          console.error('Error al registrar la revisión del pago agrupado', error);
          this.feedbackMessage = 'No se pudo registrar la revisión. Intente nuevamente.';
          this.feedbackVariant = 'danger';
          this.modalError = 'No se pudo registrar la revisión. Intenta nuevamente.';
        })
        .finally(() => {
          this.processingDecision = false;
        });
    },
    resetRevisionModal() {
      this.modalDecision = null;
      this.modalObservaciones = '';
      this.processingDecision = false;
      this.showRevisionModal = false;
      this.modalError = '';
    },
    captureBodyZoom() {
      if (typeof window === 'undefined') {
        this.bodyZoom = 1;
        return;
      }
      const { body } = document;
      if (!body) {
        this.bodyZoom = 1;
        return;
      }
      let zoomValue = 1;
      const inlineZoom = body.style && body.style.zoom;
      if (inlineZoom) {
        const parsed = parseFloat(inlineZoom);
        if (!Number.isNaN(parsed) && parsed > 0) {
          zoomValue = parsed;
        }
      } else {
        const computed = window.getComputedStyle(body);
        const computedZoom = computed && computed.zoom;
        if (computedZoom) {
          const parsedComputed = parseFloat(computedZoom);
          if (!Number.isNaN(parsedComputed) && parsedComputed > 0) {
            zoomValue = parsedComputed;
          }
        } else if (window.visualViewport && window.visualViewport.scale) {
          const viewportScale = window.visualViewport.scale;
          if (viewportScale > 0) {
            zoomValue = 1 / viewportScale;
          }
        }
      }
      this.bodyZoom = zoomValue || 1;
    },
  },
};
</script>

<style scoped>
.pagos-agrupados-view {
  text-align: left;
}

.column-right {
  background-color: #f2f7fb;
  padding-top: 80px;
  padding-left: 260px;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
}

.main-container {
  background-color: #ffffff;
  margin: 20px;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
}

.pagos-agrupados {
  padding: 24px;
}

.pagos-agrupados__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.pagos-agrupados__title {
  font-size: 24px;
  margin-bottom: 4px;
}

.pagos-agrupados__subtitle {
  margin: 0;
  color: #6c757d;
  max-width: 520px;
}

.pagos-agrupados__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pagos-agrupados__select {
  min-width: 220px;
}

.pagos-agrupados__body {
  display: flex;
  gap: 24px;
}

.pagos-agrupados__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 320px;
}

.pagos-agrupados__loading {
  display: flex;
  align-items: center;
  color: #495057;
}

.pagos-agrupados__empty {
  flex: 1;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 12px;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
}

.solicitud-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.solicitud-card:hover {
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

.solicitud-card--selected {
  border-color: #0d6efd;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
}

.solicitud-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.solicitud-card__title {
  font-size: 16px;
  margin: 0;
}

.solicitud-card__row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 6px;
}

.solicitud-card__label {
  color: #6c757d;
}

.solicitud-card__value {
  font-weight: 500;
  text-align: right;
}

.solicitud-card__value--strong {
  color: #0d6efd;
  font-weight: 600;
}

.pagos-agrupados__detail {
  flex: 1;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e9ecef;
  padding: 24px;
  min-height: 420px;
  display: flex;
  flex-direction: column;
}

.pagos-agrupados__detail-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.pagos-agrupados__detail-title {
  margin: 0;
  font-size: 22px;
}

.pagos-agrupados__detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  align-items: center;
  color: #6c757d;
}

.pagos-agrupados__detail-date {
  font-size: 13px;
}

.pagos-agrupados__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-section {
  margin-top: 24px;
}

.detail-section h4 {
  font-size: 18px;
  margin-bottom: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.detail-label {
  font-size: 13px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 15px;
  margin-top: 4px;
  font-weight: 500;
}

.detail-value--strong {
  color: #0d6efd;
  font-size: 18px;
}

.detail-subvalue {
  color: #6c757d;
  font-size: 13px;
  margin-top: 2px;
}

.detail-empty {
  padding: 16px;
  border-radius: 12px;
  background: #f8f9fa;
  color: #6c757d;
}

.comprobantes-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.comprobantes-list li {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.comprobante-nombre {
  font-weight: 600;
}

.comprobante-meta {
  font-size: 12px;
  color: #6c757d;
}

.table-responsive {
  overflow-x: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.detail-table thead th {
  text-align: left;
  background: #f1f3f5;
  padding: 10px;
  font-weight: 600;
}

.detail-table tbody td {
  padding: 12px 10px;
  border-top: 1px solid #e9ecef;
  vertical-align: top;
}

.table-main {
  font-weight: 500;
}

.table-sub {
  color: #6c757d;
  font-size: 13px;
  margin-top: 2px;
}

.text-right {
  text-align: right;
}

.revision-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 24px;
  overflow-y: auto;
  z-index: 2050;
}

.revision-modal-container {
  background: #ffffff;
  border-radius: 12px;
  width: 560px;
  max-width: 100%;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.25);
  transform-origin: top center;
  transform: scale(var(--pagos-agrupados-modal-scale, 1));
}

.revision-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 12px;
}

.revision-modal-title {
  margin: 0;
  font-size: 20px;
}

.close-btn {
  border: none;
  background: none;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  color: #8592a6;
}

.close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.revision-modal-form {
  display: flex;
  flex-direction: column;
}

.revision-modal-body {
  padding: 0 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.revision-modal-intro {
  margin: 0;
  color: #4a5568;
  font-size: 15px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-label {
  font-weight: 600;
  color: #374151;
}

.input-textarea {
  resize: vertical;
  min-height: 90px;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-family: inherit;
  font-size: 14px;
  color: #1f2937;
}

.input-textarea:disabled {
  background: #f4f6f8;
}

.modal-error {
  background: #fdecea;
  color: #c0392b;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
}

.revision-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1280px) {
  .pagos-agrupados__body {
    flex-direction: column;
  }

  .column-right {
    padding-left: 0;
    padding-top: 76px;
  }

  .pagos-agrupados__list {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .solicitud-card {
    flex: 1 1 calc(50% - 12px);
  }
}

@media (max-width: 768px) {
  .column-right {
    padding: 72px 12px 24px;
  }

  .main-container {
    margin: 0;
    padding: 16px;
  }

  .pagos-agrupados__filters {
    width: 100%;
    justify-content: flex-start;
  }

  .pagos-agrupados__select {
    flex: 1;
  }

  .pagos-agrupados__detail {
    padding: 16px;
  }

  .solicitud-card {
    flex: 1 1 100%;
  }

  .detail-grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }

  .revision-modal-container {
    transform: scale(1);
    width: 100%;
  }
}
</style>
